import { normalizeCartLinesForPdf } from './normalizeCartLinesForPdf';
import { normalizeOrderForPdf, formatPdfDisplayDate } from './normalizeOrderForPdf';
import { invoiceAmount } from './orderInvoiceMath';

function buildCreditCartLines(orderCart, creditNote) {
    if (!Array.isArray(orderCart) || !creditNote?.items?.length) {
        return [];
    }
    return orderCart
        .filter((x) => creditNote.items.some((y) => String(y.SKU) === String(x.id)))
        .map((x) => {
            const cnItem = creditNote.items.find((y) => String(y.SKU) === String(x.id));
            return { ...x, amount: cnItem?.amount ?? x.amount };
        });
}

/**
 * View-model for {@link CreditNotePdfDocument}. Pass `creditNote` from `order.creditNotes`.
 * When `creditNote` is omitted (e.g. preview), returns an empty credit skeleton with billing/delivery from the order.
 *
 * @param {Record<string, unknown>} order
 * @param {Record<string, unknown> | undefined} creditNote
 * @param {{ old?: boolean }} [options]
 */
export function normalizeCreditNoteForPdf(order, creditNote, options = {}) {
    const old = Boolean(options.old);
    const base = normalizeOrderForPdf(order, { old, variant: 'order' });

    if (!creditNote) {
        return {
            orderNumber: base.orderNumber,
            invoiceNumber: base.invoiceNumber,
            status: base.status,
            orderDate: base.orderDate,
            note: base.note,
            billing: base.billing,
            delivery: base.delivery,
            creditNoteNumber: '—',
            creditNoteDate: '',
            cartLines: [],
            credit: {
                itemsSubtotal: 0,
                surcharge: 0,
                shipping: 0,
                vat18: 0,
                combined: 0,
                method: base.invoice.method,
                paymentStatus: '',
            },
        };
    }

    const creditCart = buildCreditCartLines(order?.cart || [], creditNote);
    const itemsSubtotal = creditCart.reduce((s, row) => {
        const qty = Number(row.amount ?? 0);
        const price = Number(row.price ?? 0);
        return s + qty * price;
    }, 0);
    const surcharge = invoiceAmount(creditNote?.surcharge);
    const shipping = invoiceAmount(creditNote?.shipping);
    const combined = itemsSubtotal + surcharge + shipping;
    const vat18 = combined > 0 ? (combined / 1.18) * 0.18 : 0;

    return {
        orderNumber: base.orderNumber,
        invoiceNumber: base.invoiceNumber,
        status: base.status,
        orderDate: base.orderDate,
        note: base.note,
        billing: base.billing,
        delivery: base.delivery,
        creditNoteNumber: String(creditNote.creditNoteNumber ?? ''),
        creditNoteDate:
            creditNote?.date != null ? formatPdfDisplayDate(creditNote.date) : '',
        cartLines: normalizeCartLinesForPdf(creditCart),
        credit: {
            itemsSubtotal,
            surcharge,
            shipping,
            vat18,
            combined,
            method: base.invoice.method,
            paymentStatus:
                creditNote?.paymentStatus != null ? String(creditNote.paymentStatus) : '',
        },
    };
}
